/**
 * LCC2 export that keeps XGRIDS' LOD levels: every splat file of the original is
 * read, each splat inherits the edit state of its anchor (the nearest splat of the
 * loaded LOD), and the file is re-encoded in node order. See
 * docs/superpowers/specs/2026-09-14-lcc2-export-design.md
 */

import {
    createChunkDataPool,
    materializeToDataTable,
    readFile,
    Transform,
    writeSog,
    ZipReadFileSystem,
    type ChunkData,
    type ChunkDataPool,
    type ChunkLayer,
    type ChunkSource,
    type ChunkSourceMetadata,
    type DataTable,
    type DeviceCreator,
    type FileSystem,
    type ReadFileSystem,
    type ReadRequest
} from '@playcanvas/splat-transform';

import { bakeGaussians, buildLayouts, identityGrade, SH_REST_COUNTS, type GradeLookup, type TransformLookup } from '../gaussian-bake';
import { AnchorGrid } from './anchor-grid';
import { EditState } from './edit-state';
import { serializeLcc2Index, type Lcc2File, type Lcc2Index, type NodeResult } from './lcc2-index';

type Lcc2Edits = {
    loadedLevel: number;
    state: EditState;
    transforms: TransformLookup[];
    grades: GradeLookup[];
    envTransform: TransformLookup | null;
};

type Lcc2ExportOptions = {
    index: Lcc2Index;
    indexName: string;
    readFs: ReadFileSystem;
    writeFs: FileSystem;
    edits: Lcc2Edits;
    copyFiles: string[];
    iterations: number;
    createDevice?: DeviceCreator;
    onProgress?: (done: number, total: number, label: string) => void | Promise<void>;
};

const READ_OPTIONS = { iterations: 10, lodSelect: [] as number[], unbundled: false, lodChunkCount: 512, lodChunkExtent: 16 };

const BAKE_CHUNK_SIZE = 256 * 1024;

const basename = (path: string) => path.split('/').pop();

const dirname = (path: string) => path.split('/').slice(0, -1).join('/');

// dropped folders keep relative paths, picked files only their names
// (splat-transform's LCC2 reader falls back the same way)
const createSource = async (fs: ReadFileSystem, path: string) => {
    try {
        return await fs.createSource(path);
    } catch (err) {
        if (basename(path) === path) throw err;
        return await fs.createSource(basename(path));
    }
};

const openSog = async (fs: ReadFileSystem, path: string) => {
    if (!path.toLowerCase().endsWith('.sog')) {
        throw new Error(`Unsupported LCC2 chunk file ${path} (only .sog is supported)`);
    }
    const zip = new ZipReadFileSystem(await createSource(fs, path));
    try {
        const [source] = await readFile({ filename: 'meta.json', inputFormat: 'sog', options: READ_OPTIONS, params: [], fileSystem: zip });
        return {
            source,
            close: async () => {
                await source.close();
                zip.close();
            }
        };
    } catch (err) {
        zip.close();
        throw err;
    }
};

const checkCount = (source: ChunkSource, file: Lcc2File) => {
    if (source.meta.numGaussians !== file.count) {
        throw new Error(`LCC2 file ${file.path}: index lists ${file.count} splats, file has ${source.meta.numGaussians}`);
    }
};

const readPositions = async (source: ChunkSource, target: Float32Array, offset: number) => {
    const { meta } = source;
    const pool = createChunkDataPool({ chunkSize: meta.chunkSize });
    try {
        for (let chunkIndex = 0; chunkIndex < meta.numChunks[0]; ++chunkIndex) {
            const count = Math.min(meta.chunkSize, meta.numGaussians - chunkIndex * meta.chunkSize);
            const position = pool.acquire('position', meta.layouts.position, count);
            try {
                await source.read({ chunkIndex, position });
                target.set(new Float32Array(position.data, 0, count * 3), offset + chunkIndex * meta.chunkSize * 3);
            } finally {
                position.release();
            }
        }
    } finally {
        pool.destroy();
    }
};

const writeBytes = async (fs: FileSystem, path: string, data: Uint8Array) => {
    if (dirname(path)) await fs.mkdir(dirname(path));
    const writer = await fs.createWriter(path);
    try {
        await writer.write(data);
    } catch (err) {
        await writer.abort();
        throw err;
    }
    await writer.close();
};

const copyFile = async (readFs: ReadFileSystem, writeFs: FileSystem, path: string) => {
    const source = await createSource(readFs, path);
    try {
        await writeBytes(writeFs, path, await source.read().readAll());
    } finally {
        source.close();
    }
};

/**
 * Output rows of one file as a ChunkSource: row i is input row `rows[i]` baked with
 * the transform and grade of `instances[i]` in layer `layers[i]`. Output stays in
 * the input's own space and is tagged PLY, so no writer bakes anything on top.
 */
class BakedSource implements ChunkSource {
    readonly meta: ChunkSourceMetadata;

    private readonly pool: ChunkDataPool;

    constructor(
        private readonly input: ChunkSource,
        private readonly rows: Uint32Array,
        private readonly layers: Uint16Array,
        private readonly instances: Uint32Array,
        private readonly transforms: TransformLookup[],
        private readonly grades: GradeLookup[]
    ) {
        const n = rows.length;
        const { shBands } = input.meta;
        this.pool = createChunkDataPool({ chunkSize: input.meta.chunkSize });
        this.meta = {
            numGaussians: n,
            numLods: 1,
            lodCounts: [n],
            chunkSize: BAKE_CHUNK_SIZE,
            numChunks: [Math.ceil(n / BAKE_CHUNK_SIZE)],
            shBands,
            model: input.meta.model,
            extraColumns: [],
            transform: Transform.PLY,
            availableLayers: new Set<ChunkLayer>(['position', 'geometric', 'color']),
            layouts: buildLayouts(SH_REST_COUNTS[shBands])
        };
    }

    async read(request: ReadRequest): Promise<void> {
        if ('indices' in request) throw new Error('BakedSource supports sequential reads only');
        const anyBuffer = (request.position ?? request.geometric ?? request.color) as ChunkData;
        const base = request.chunkIndex * BAKE_CHUNK_SIZE;
        const { count } = anyBuffer;
        let offset = 0;
        while (offset < count) {
            const layer = this.layers[base + offset];
            let n = 1;
            while (offset + n < count && this.layers[base + offset + n] === layer) n++;
            await bakeGaussians(
                this.input,
                this.rows.subarray(base + offset, base + offset + n),
                this.instances.subarray(base + offset, base + offset + n),
                this.transforms[layer],
                this.grades[layer],
                this.pool,
                request,
                offset,
                SH_REST_COUNTS[this.meta.shBands]
            );
            offset += n;
        }
    }

    close(): Promise<void> {
        this.pool.destroy();
        return Promise.resolve();
    }
}

type FilePlan = { rows: Uint32Array, layers: Uint16Array, instances: Uint32Array, nodeCounts: number[] };

// `positions` null means the file belongs to the loaded LOD: rows are their own anchors
const planFile = (file: Lcc2File, positions: Float32Array | null, state: EditState, grid: AnchorGrid): FilePlan => {
    const anchors = new Uint32Array(file.count);
    let total = 0;
    for (let j = 0; j < file.count; ++j) {
        const anchor = positions ?
            grid.nearest(positions[j * 3], positions[j * 3 + 1], positions[j * 3 + 2]) :
            file.streamOffset + j;
        anchors[j] = anchor;
        total += state.offsets[anchor + 1] - state.offsets[anchor];
    }

    const rows = new Uint32Array(total);
    const layers = new Uint16Array(total);
    const instances = new Uint32Array(total);
    const nodeCounts: number[] = [];
    let out = 0;
    for (const node of file.nodes) {
        const first = out;
        for (let j = node.start; j < node.start + node.count; ++j) {
            const anchor = anchors[j];
            for (let k = state.offsets[anchor]; k < state.offsets[anchor + 1]; ++k) {
                rows[out] = j;
                layers[out] = state.layer[k];
                instances[out] = state.instance[k];
                out++;
            }
        }
        nodeCounts.push(out - first);
    }
    return { rows, layers, instances, nodeCounts };
};

const nodeResults = (dt: DataTable, counts: number[]): NodeResult[] => {
    const x = dt.getColumnByName('x').data;
    const y = dt.getColumnByName('y').data;
    const z = dt.getColumnByName('z').data;
    let row = 0;
    return counts.map((count) => {
        if (count === 0) return { count, box: null };
        const min: [number, number, number] = [Infinity, Infinity, Infinity];
        const max: [number, number, number] = [-Infinity, -Infinity, -Infinity];
        for (const end = row + count; row < end; ++row) {
            min[0] = Math.min(min[0], x[row]);
            min[1] = Math.min(min[1], y[row]);
            min[2] = Math.min(min[2], z[row]);
            max[0] = Math.max(max[0], x[row]);
            max[1] = Math.max(max[1], y[row]);
            max[2] = Math.max(max[2], z[row]);
        }
        return { count, box: { min, max } };
    });
};

// bake a planned file and encode it in plan order (no morton sort, nodes stay contiguous)
const bakeAndWrite = async (
    options: Lcc2ExportOptions,
    source: ChunkSource,
    path: string,
    plan: FilePlan,
    transforms: TransformLookup[],
    grades: GradeLookup[]
) => {
    const baked = new BakedSource(source, plan.rows, plan.layers, plan.instances, transforms, grades);
    const pool = createChunkDataPool({ chunkSize: baked.meta.chunkSize });
    try {
        const dataTable = await materializeToDataTable(baked, pool);
        const results = nodeResults(dataTable, plan.nodeCounts);
        if (dirname(path)) await options.writeFs.mkdir(dirname(path));
        const indices = new Uint32Array(dataTable.numRows);
        for (let i = 0; i < indices.length; ++i) indices[i] = i;
        await writeSog({
            filename: path,
            bundle: true,
            iterations: options.iterations,
            dataTable,
            indices,
            createDevice: options.createDevice,
            logging: 'silent'
        }, options.writeFs);
        return results;
    } finally {
        pool.destroy();
        await baked.close();
    }
};

const exportLcc2 = async (options: Lcc2ExportOptions) => {
    const { index, readFs, writeFs, edits } = options;
    const loaded = index.levels[edits.loadedLevel];
    if (!loaded?.length) throw new Error(`LCC2 LOD ${edits.loadedLevel} not found`);
    const numLoaded = index.json.lodSplats[edits.loadedLevel];
    if (edits.state.offsets.length - 1 !== numLoaded) {
        throw new Error(`Edit state covers ${edits.state.offsets.length - 1} splats, LOD ${edits.loadedLevel} has ${numLoaded}`);
    }

    const total = index.files.length + 3;
    let done = 0;
    const step = async (label: string) => {
        await options.onProgress?.(done++, total, label);
    };

    await step('anchors');
    const loadedPositions = new Float32Array(numLoaded * 3);
    for (const file of loaded) {
        const { source, close } = await openSog(readFs, file.path);
        try {
            checkCount(source, file);
            await readPositions(source, loadedPositions, file.streamOffset * 3);
        } finally {
            await close();
        }
    }
    const grid = new AnchorGrid(loadedPositions);

    const files = new Map<number, NodeResult[]>();
    for (const file of index.files) {
        await step(file.path);
        const { source, close } = await openSog(readFs, file.path);
        try {
            checkCount(source, file);
            let positions: Float32Array | null = null;
            if (file.level !== edits.loadedLevel) {
                positions = new Float32Array(file.count * 3);
                await readPositions(source, positions, 0);
            }
            const plan = planFile(file, positions, edits.state, grid);
            files.set(file.index, plan.rows.length === 0 ?
                file.nodes.map((): NodeResult => ({ count: 0, box: null })) :
                await bakeAndWrite(options, source, file.path, plan, edits.transforms, edits.grades));
        } finally {
            await close();
        }
    }

    await step('env');
    let env: NodeResult | null = null;
    if (index.envFile !== null) {
        const path = index.json.root.splatFiles[index.envFile];
        if (!edits.envTransform) {
            await copyFile(readFs, writeFs, path);
        } else {
            const { source, close } = await openSog(readFs, path);
            try {
                const n = source.meta.numGaussians;
                const rows = new Uint32Array(n);
                for (let i = 0; i < n; ++i) rows[i] = i;
                const plan = { rows, layers: new Uint16Array(n), instances: new Uint32Array(n), nodeCounts: [n] };
                [env] = await bakeAndWrite(options, source, path, plan, [edits.envTransform], [identityGrade()]);
            } finally {
                await close();
            }
        }
    }

    await step('index');
    await writeBytes(writeFs, options.indexName, new TextEncoder().encode(serializeLcc2Index(index, { files, env })));
    for (const path of options.copyFiles) {
        await copyFile(readFs, writeFs, path);
    }
    await options.onProgress?.(total, total, 'done');
};

export { exportLcc2, type Lcc2Edits, type Lcc2ExportOptions };
